import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import FinanceNote from "@/components/FinanceNote";
import { beyondGrants as c } from "@/content/beyondGrants";

const CONTACT_URL = "/contact?topic=funding-grants";

export default function BeyondGrants() {
  return (
    <>
      <Seo
        title="Regenerative Finance & Funding Strategies | Bioregional Ventures"
        description="Explore catalytic capital, patient finance, community asset funding, blended finance and regenerative investment approaches with Bioregional Ventures."
      />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16 md:pb-20">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.15em] text-ochre-dark mb-4">{c.eyebrow}</p>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight text-charcoal max-w-3xl" data-testid="bg-heading">
            {c.title}
          </h1>
          <p className="mt-4 font-serif italic text-xl md:text-2xl text-ochre-dark max-w-2xl">
            {c.standfirst}
          </p>
          {c.heroBody.map((p) => (
            <p key={p.slice(0, 24)} className="mt-5 text-base md:text-lg leading-relaxed text-charcoal/90 max-w-2xl">
              {p}
            </p>
          ))}
          <Link
            to={CONTACT_URL}
            data-testid="bg-hero-cta"
            className="mt-10 inline-block bg-eucalyptus text-cream px-7 py-3.5 rounded-sm text-sm tracking-wide hover:bg-eucalyptus-deep transition-colors duration-300"
          >
            {c.heroCta}
          </Link>
        </Reveal>
      </section>

      {/* Why different approaches + pull quote */}
      <section className="bg-sage/60 border-y border-charcoal/10">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="bg-why-heading">
              {c.whyHeading}
            </h2>
            {c.whyBody.map((p) => (
              <p key={p.slice(0, 24)} className="mt-5 text-base md:text-lg leading-relaxed text-charcoal/90">
                {p}
              </p>
            ))}
            <blockquote className="mt-10 border-l-2 border-ochre pl-6 font-serif italic text-xl md:text-2xl leading-snug text-charcoal" data-testid="bg-pull-quote">
              {c.pullQuote}
            </blockquote>
          </Reveal>
        </div>
      </section>

      {/* Six financing approaches */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <Reveal>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="bg-approaches-heading">
            {c.approachesHeading}
          </h2>
        </Reveal>
        <div className="mt-12 space-y-0 divide-y divide-charcoal/10 border-y border-charcoal/10">
          {c.approaches.map((a, i) => (
            <Reveal key={a.title} delay={i * 0.03}>
              <article className="py-9 md:py-11 grid grid-cols-[3rem_1fr] gap-5" data-testid={`bg-approach-${i + 1}`}>
                <span className="font-serif text-ochre text-2xl leading-none pt-1">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3 className="font-serif text-xl md:text-2xl leading-snug text-charcoal">{a.title}</h3>
                  <p className="mt-3 text-base leading-relaxed text-charcoal/85 max-w-3xl">{a.text}</p>
                  <p className="mt-4 text-sm leading-relaxed text-charcoal/70 border-l-2 border-ochre pl-4 max-w-3xl">
                    {a.role}
                  </p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Illustrative scenarios */}
      <section className="bg-sage/60 border-y border-charcoal/10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.15em] text-ochre-dark" data-testid="bg-scenarios-label">
              {c.scenariosLabel}
            </p>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {c.scenarios.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.06} className="h-full">
                <div className="h-full border border-charcoal/15 bg-cream p-7 md:p-8" data-testid={`bg-scenario-${String.fromCharCode(97 + i)}`}>
                  <h3 className="font-serif text-xl md:text-2xl leading-snug text-charcoal">{s.title}</h3>
                  <p className="mt-4 text-sm md:text-base leading-relaxed text-charcoal/85">{s.intro}</p>
                  <ol className="mt-4 space-y-2 text-sm leading-relaxed text-charcoal/85 list-decimal pl-5">
                    {s.steps.map((step) => (
                      <li key={step}>{step}</li>
                    ))}
                  </ol>
                  <p className="mt-5 text-xs leading-relaxed text-charcoal/60 border-t border-charcoal/10 pt-4">
                    {s.role}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Related pages + final CTA */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.15em] text-ochre-dark" data-testid="bg-related-label">
            {c.relatedPagesHeading}
          </p>
          <nav aria-label="Related pages" className="mt-4 flex flex-wrap gap-x-8 gap-y-3" data-testid="bg-related-pages">
            {c.relatedPages.map((r, i) => (
              <Link
                key={r.href}
                to={r.href}
                data-testid={`bg-related-link-${i + 1}`}
                className="text-sm tracking-wide text-ochre-dark underline decoration-ochre underline-offset-4 hover:text-charcoal transition-colors duration-300"
              >
                {r.label}
              </Link>
            ))}
          </nav>
        </Reveal>
        <Reveal className="mt-14 text-center">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight leading-snug text-charcoal" data-testid="bg-cta-heading">
            {c.ctaHeading}
          </h2>
          <p className="mt-5 text-base leading-relaxed text-charcoal/90">{c.ctaBody}</p>
          <Link
            to={CONTACT_URL}
            data-testid="bg-final-cta"
            className="mt-8 inline-block bg-eucalyptus text-cream px-8 py-3.5 rounded-sm text-sm tracking-wide hover:bg-eucalyptus-deep transition-colors duration-300"
          >
            {c.ctaButton}
          </Link>
        </Reveal>
        <FinanceNote />
      </section>
    </>
  );
}
